import { useMemo } from 'react'
import { Link } from 'react-router'
import { LiveSearchBadge } from '@/components/LiveSearchBadge'
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion'
import type { PerformanceSource } from '@/lib/performance.types'
import {
  formatMetricPercent,
  formatPerformanceTimestamp,
  formatWeightedRaw,
} from '@/lib/performanceFormat'
import { useTrackerHref } from '@/provider/useTrackerHref'
import type { PerformanceRunTableProps } from './PerformanceRunTable.types'

const SOURCE_LABEL: Record<PerformanceSource, string> = {
  on_demand: 'Manual',
  scheduled: 'Scheduled',
}

export function PerformanceRunTable({ points }: PerformanceRunTableProps) {
  const href = useTrackerHref()

  // Newest first for the table; the chart order (oldest first) is untouched.
  const rows = useMemo(() => [...points].reverse(), [points])

  return (
    <Accordion type="single" collapsible className="mt-8">
      <AccordionItem
        value="runs"
        className="border border-border rounded-lg bg-card px-4"
      >
        <AccordionTrigger>
          <span className="text-sm font-medium">
            Runs{' '}
            <span className="text-muted-foreground font-normal">
              ({points.length})
            </span>
          </span>
        </AccordionTrigger>
        <AccordionContent>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border text-left text-xs text-muted-foreground">
                  <th className="py-2 pr-4 font-medium">Measurement (UTC)</th>
                  <th className="py-2 pr-4 font-medium">Source</th>
                  <th className="py-2 pr-4 font-medium">Visibility Rate</th>
                  <th className="py-2 pr-4 font-medium">Weighted Score</th>
                  <th className="py-2 pr-4 font-medium">Share of Voice</th>
                  <th className="py-2 pr-4 font-medium">Queries</th>
                  <th className="py-2 pr-4 font-medium">Engines</th>
                  <th className="py-2 pr-4 font-medium">Run detail</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((point) => (
                  <tr
                    key={point.runId}
                    className="border-b border-border last:border-0"
                  >
                    <td className="py-2 pr-4 whitespace-nowrap">
                      <time dateTime={point.measurementAt}>
                        {formatPerformanceTimestamp(point.measurementAt)}
                      </time>
                    </td>
                    <td className="py-2 pr-4">
                      <span className="inline-flex items-center gap-1.5">
                        {SOURCE_LABEL[point.source]}
                        {point.liveSearch && <LiveSearchBadge />}
                      </span>
                    </td>
                    <td className="py-2 pr-4 tabular-nums">
                      {formatMetricPercent(point, 'visibilityRate')}
                    </td>
                    <td className="py-2 pr-4 tabular-nums">
                      {formatWeightedRaw(
                        point.weightedScore,
                        point.weightedScoreMax
                      )}{' '}
                      ({formatMetricPercent(point, 'weightedScorePercent')})
                    </td>
                    <td className="py-2 pr-4 tabular-nums">
                      {formatMetricPercent(point, 'shareOfVoice')}
                    </td>
                    <td className="py-2 pr-4 tabular-nums">
                      {point.queryCount}
                    </td>
                    <td className="py-2 pr-4 tabular-nums">
                      {point.engineCount}
                    </td>
                    <td className="py-2 pr-4">
                      <Link
                        to={href(`/run/${point.runId}`)}
                        className="font-medium text-primary hover:underline"
                      >
                        View
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </AccordionContent>
      </AccordionItem>
    </Accordion>
  )
}
